import axios from 'axios'
import PropTypes from 'prop-types'
import find from 'lodash/find'
import mapValues from 'lodash/mapValues'
import { API_BASE_URL, API_BASE_URL_V2 } from 'config'
import { kea } from 'kea'
import { put, call } from 'redux-saga/effects'
import { searchToObject, csObjectFromUnderscore } from 'utils'
import { omit } from 'lodash'
import qs from 'querystring'

/** Contants */
const DEFAULT_VALUES = {}
const DEFAULT_SETTINGS = {
  iframed: false,
  goToResults: false,
  electricCar: true,
}

const OFFER_TYPES_ENDPOINT = `${API_BASE_URL}/public/offer-type/`
const TRANSLATIONS_ENDPOINT = `${API_BASE_URL}/public/translations/`
const ME_ENDPOINT = `${API_BASE_URL}/private/user-profile/me/`
const TRANSLATIONS_LOCAL_KEY = 'local'
const AUTH_TOKEN_KEY = `tokenauth`
const LOADING_COUNT = 3
const SIMILATIONS_ENPOINT = `${API_BASE_URL_V2}/public/simulation/{code}`

/* eslint-disable camelcase */
const Logic = kea({
  path: () => ['scenes', 'appV2'],
  actions: () => ({
    getAppTranslations: () => ({}),
    getCurrentUser: () => ({}),
    setAppTranslations: (translations) => ({ translations }),

    setValue: (selected, item) => ({ values: { [item]: selected.value } }),
    setValues: (selected) => selected,
    setIsUserLoggedIn: (is) => ({ is }),

    setFinished: (value) => ({ value }),

    setOfferTypes: (offerTypes) => ({ offerTypes }),
    fetchOfferTypes: () => ({}),
    fetchError: (error) => ({ error }),
    setStarted: () => ({}),
    setSettings: (settings) => ({ settings }),

    decreaseLoading: () => ({}),

    changeSimulationData: () => ({}),
  }),

  reducers: ({ actions }) => ({
    values: [
      DEFAULT_VALUES,
      PropTypes.object,
      {
        [actions.setValue]: (state, payload) => ({
          ...state,
          ...payload.values,
        }),
        [actions.setValues]: (state, payload) => ({ ...state, ...payload }),
      },
    ],

    offerTypes: [
      null,
      PropTypes.any,
      {
        [actions.setOfferTypes]: (_, payload) => payload.offerTypes,
      },
    ],

    finished: [
      {},
      PropTypes.bool,
      {
        [actions.setFinished]: (_, payload) => ({ ..._, ...payload.value }),
      },
    ],

    translations: [
      {},
      PropTypes.object,
      {
        [actions.setAppTranslations]: (state, payload) => payload.translations,
      },
    ],

    userLoggedIn: [
      false,
      PropTypes.any,
      {
        [actions.setIsUserLoggedIn]: (_, payload) => payload.is,
      },
    ],

    started: [
      false,
      PropTypes.bool,
      {
        [actions.setStarted]: () => true,
      },
    ],

    settings: [
      DEFAULT_SETTINGS,
      PropTypes.object,
      {
        [actions.setSettings]: (state, payload) => ({
          ...state,
          ...payload.settings,
        }),
      },
    ],

    loading: [
      LOADING_COUNT,
      PropTypes.number,
      {
        [actions.decreaseLoading]: (state) => state - 1,
      },
    ],

    dataSimulationChanged: [
      false,
      PropTypes.bool,
      {
        [actions.changeSimulationData]: () => true,
      },
    ],
  }),

  takeLatest: ({ actions, workers }) => ({
    [actions.fetchOfferTypes]: workers.fetchOfferTypes,
    [actions.getAppTranslations]: workers.getAppTranslations,
    [actions.getCurrentUser]: workers.getCurrentUser,
  }),
  start: function*() {
    const {
      fetchOfferTypes,
      setAppTranslations,
      getAppTranslations,
      getCurrentUser,
      setStarted,
      setSettings,
      decreaseLoading,
      setFinished,
      setValues,
    } = this.actions

    const appStarted = yield this.get('started')

    // Importing App makes the app do stuff again thats not needed
    // Using this we can prevent it
    if (appStarted) {
      return false
    }

    yield put(fetchOfferTypes())
    yield put(getCurrentUser())

    // Check if user has translations in localStorage
    // We need to do this because of performance issues
    const localTranslations = window.localStorage.getItem(
      TRANSLATIONS_LOCAL_KEY
    )

    if (localTranslations) {
      const parsedTranslations = JSON.parse(localTranslations)
      yield put(setAppTranslations(parsedTranslations))
    }

    // Check if there are any params in the first request
    const params = searchToObject(window.location.search)

    // Set iframe settings
    if (params.iframed) {
      document.body.classList.add('iframed')
      yield put(setSettings({ iframed: true }))
    }

    if (!params.profile) {
      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)

      try {
        const s = urlParams.get('code')
        if (s) {
          yield put(setSettings({ isFromUrl: true, goToResults: true }))
          const response = yield call(
            axios.get,
            SIMILATIONS_ENPOINT.replace('{code}', s)
          )

          let values = csObjectFromUnderscore(
            response.data.simulation_history.api_input_data
          )

          values = omit(values, ['simulationCode'])

          yield put(setValues(values))
          yield put(
            setFinished({
              profileFinished: true,
            })
          )
        }
      } catch (error) {}

      yield put(decreaseLoading())
    }

    // We still want to update the translations anyway
    // But if user already visited we don't want unnecessary load time
    yield put(getAppTranslations())
    yield put(setStarted(true))
  },

  workers: {
    *setValuesUrl() {
      const values = yield this.get('values')
      const query = qs.stringify(values)
      const url = `?s=${btoa(query)}`
      window.history.pushState('', '', url + window.location.hash)
    },

    /**
     * Fetches offer type for the overall use in app
     */
    *fetchOfferTypes() {
      const { setOfferTypes, fetchError, decreaseLoading } = this.actions
      try {
        const response = yield call(axios.get, OFFER_TYPES_ENDPOINT, {
          limit: 1000,
        })
        const { results } = response.data

        if (results) {
          yield put(setOfferTypes(results))
          yield put(decreaseLoading())
        }
      } catch (error) {
        yield put(fetchError(error))
      }
    },

    /**
     * Gets app translations
     */
    *getAppTranslations() {
      const { setAppTranslations, setFatalError } = this.actions

      try {
        const request = yield call(axios.get, TRANSLATIONS_ENDPOINT)
        const results = request.data.results

        // Convert translations to a simpler object
        // This can be optimized #todo
        let translations = {}
        mapValues(results, (item) => {
          return mapValues(item.properties, (prop) => {
            translations[prop.key] = prop.value
          })
        })

        // Get local translations
        // We don't want the user to see stuff changing when hes using the app
        // Only update the storage on background and the next time he visits
        // He will have the translations updated
        const localTranslations = window.localStorage.getItem(
          TRANSLATIONS_LOCAL_KEY
        )
        if (!localTranslations) {
          yield put(setAppTranslations(translations))
        }

        // Set the new translations to localstorage
        window.localStorage.setItem(
          TRANSLATIONS_LOCAL_KEY,
          JSON.stringify(translations)
        )
      } catch (error) {
        yield put(setFatalError())
      }
    },

    /**
     * Gets current user logged in
     */
    *getCurrentUser() {
      const { setIsUserLoggedIn, decreaseLoading } = this.actions
      const { setSettings } = this.actions
      try {
        const token = window.localStorage.getItem(AUTH_TOKEN_KEY)
        const params = searchToObject(window.location.search)

        if (!token) {
          yield put(setIsUserLoggedIn(false))
          yield put(decreaseLoading())

          if (params.profile) {
            yield put(decreaseLoading())
          }
          return
        }

        const me = yield call(axios.get, ME_ENDPOINT, {
          headers: {
            Authorization: `JWT ${token}`,
          },
        })

        yield put(setIsUserLoggedIn(me.data))
        axios.defaults.headers.common['Authorization'] = `JWT ${token}`

        if (params.profile) {
          yield put(setSettings({ isFromProfile: params.profile }))
        }
        yield put(decreaseLoading())
      } catch (error) {
        yield put(setIsUserLoggedIn(false))
        localStorage.removeItem(AUTH_TOKEN_KEY)
        delete axios.defaults.headers.common['Authorization']
        yield put(decreaseLoading())
      }
    },
  },

  selectors: ({ selectors }) => ({
    /**
     * Selects the current offer type object based on id
     */
    offerType: [
      () => [selectors.values, selectors.offerTypes],
      ({ offerType }, offerTypes) => {
        return find(offerTypes, ({ id }) => id === offerType)
      },
      PropTypes.any,
    ],

    iframed: [
      () => [selectors.settings],
      ({ iframed }) => iframed,
      PropTypes.any,
    ],

    electricCar: [
      () => [selectors.settings],
      ({ electricCar }) => electricCar,
      PropTypes.any,
    ],

    goToResults: [
      () => [selectors.settings],
      ({ goToResults }) => goToResults,
      PropTypes.any,
    ],
  }),
})

export default Logic



// WEBPACK FOOTER //
// ./src/containers/AppV2/logic.js